import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';

import { useBreakpoint } from '@techstyle/react-components';

import { mobile } from '../../styles';
import { BasicButton } from '../../styles/button';
import { Component as Carousel } from '../Carousel';
import { Component as CloseButton } from '../CloseButton';
import ModelThumbnail from '../ModelThumbnail/ModelThumbnail';
import { Component as ProductDetailPlayButton } from '../ProductDetailPlayButton';
import ProductVideo, { VideoCoverSizes } from '../ProductVideo/ProductVideo';

const THUMBNAIL_NEW_RATIO_MARGIN_XLARGE = 128;

const Wrapper = styled.section`
  ${({ isFullScreen, isClickToZoom, theme }) => `
    background-color: ${theme.colors.default};

    ${
      isFullScreen && isClickToZoom
        ? css`
            bottom: 0;
            left: 0;
            position: fixed;
            right: 0;
            top: 0;
            z-index: 200;
          `
        : ''
    }
  `}
`;

const StyledCarousel = styled(Carousel)`
  ${({ isFullScreen }) =>
    isFullScreen
      ? css`
          height: 100%;

          .slick-slider,
          .slick-list,
          .slick-track,
          .slick-slide,
          .slick-slide > div {
            height: 100%;
          }

          .slick-dots {
            bottom: ${props => props.theme.spacing.large}px;
          }
        `
      : ''}

  .slick-dots li button:before {
    box-shadow: none;
  }
  .slick-dots li.slick-active button:before {
    box-shadow: none;
  }

  @media (min-width: ${props => props.theme.breakpoints.xlarge}px) {
    float: left;
    width: calc(100% - ${THUMBNAIL_NEW_RATIO_MARGIN_XLARGE}px);

    &:after {
      content: '';
      display: table;
      clear: both;
    }
  }
`;

const StyledModelThumbnail = styled(ModelThumbnail)`
  display: block !important;
`;

const ProductThumbnailVideo = styled(ProductVideo)`
  display: block;
  height: auto;
  max-width: 100%;
`;

const StyledCloseButton = styled(CloseButton)`
  background-color: ${props => props.theme.colors.whiteSubdued};
  border-radius: 2px;
  height: 44px;
  line-height: 0;
  right: 10px;
  top: 10px;
  width: 44px;
  z-index: 1;
`;

const FullScreenButton = styled(BasicButton)`
  background-color: transparent;
  border: unset;
  bottom: 0;
  height: unset;
  left: 0;
  padding: 0;
  position: absolute;
  right: 0;
  top: 0;
  width: 100%;
  z-index: 1;

  ${mobile`
    &:hover {
      background-color: transparent;
    }
  `}
`;

function ProductImageCarouselFullScreen({
  forwardedRef,
  handleBeforeImageCarouselChange = () => {},
  handleModelImageFullScreen = () => {},
  isModelImageFullScreen,
  model,
  modelAngles,
  options,
  theme,
}) {
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [isZoomed, setIsZoomed] = useState(false);
  const breakpoint = useBreakpoint('(max-width: 1023px)');
  const isClickToZoom = breakpoint.matches;

  useEffect(() => {
    // reset isModelImageFullScreen whenever the defined breakpoint changes
    if (isClickToZoom || !isClickToZoom) {
      handleModelImageFullScreen(false);
    }
  }, [handleModelImageFullScreen, isClickToZoom]);

  const handleModelClick = () => {
    if (isClickToZoom && !isModelImageFullScreen) {
      handleModelImageFullScreen(true);
    }
  };
  const handleAfterChange = newIndex => {
    setActiveImageIndex(newIndex);
    setIsZoomed(false);
  };

  const onZoomChange = _isZoomed => {
    setIsZoomed(_isZoomed);
  };

  const thumbnailOptions = {
    arrows: false,
    dots: false,
    responsive: [
      {
        // For some reason, breakpoints don't seem to work unless there are
        // at least two.
        breakpoint: theme.breakpoints.xxlarge,
        settings: {
          dots: false,
        },
      },
      {
        breakpoint: theme.breakpoints.xlarge - 1,
        settings: {
          customPaging: Carousel.defaultOptions.customPaging,
          dots: true,
        },
      },
    ],
    swipe: !isZoomed,
    touchMove: !isZoomed,
  };
  const sliderProps = {
    ...thumbnailOptions,
    ...options,
    beforeChange: handleBeforeImageCarouselChange,
    afterChange: handleAfterChange,
  };
  const { vimeo_id: vimeoId } = model.product;

  return (
    <Wrapper
      isClickToZoom={isClickToZoom}
      isFullScreen={isModelImageFullScreen}
    >
      {isModelImageFullScreen ? (
        <StyledCloseButton
          onClick={() => handleModelImageFullScreen(false)}
          data-autotag="pdpCloseFullScreen"
        />
      ) : null}
      <StyledCarousel
        // The Slick component handles rendering different slides and
        // immediately calling `goToSlide` poorly, so just remount it
        // entirely when the model changes.
        isFullScreen={isModelImageFullScreen}
        dataTestId="ImageCarousel"
        key={model.modelId}
        options={sliderProps}
        sliderRef={forwardedRef}
      >
        {modelAngles.map((angle, i) => {
          if (angle === 'video') {
            return (
              <ProductThumbnailVideo
                product={model.product}
                vimeoId={vimeoId}
                size={VideoCoverSizes.LARGE}
                playIcon={<ProductDetailPlayButton overlay large />}
                key={`${angle}-${i}`}
              />
            );
          }

          return (
            <React.Fragment key={`${angle}-${i}`}>
              <StyledModelThumbnail
                activeImageIndex={activeImageIndex}
                isModelImageFullScreen={isModelImageFullScreen}
                isClickToZoom={isClickToZoom}
                model={model}
                angle={angle}
                isZoomable
                onZoomChange={onZoomChange}
              />
              {!isModelImageFullScreen && isClickToZoom ? (
                <FullScreenButton
                  aria-label="Full screen"
                  onClick={handleModelClick}
                  data-autotag="pdpFullScreen"
                />
              ) : null}
            </React.Fragment>
          );
        })}
      </StyledCarousel>
    </Wrapper>
  );
}

ProductImageCarouselFullScreen.propTypes = {
  forwardedRef: PropTypes.any,
  handleBeforeImageCarouselChange: PropTypes.any,
  handleModelImageFullScreen: PropTypes.func,
  isModelImageFullScreen: PropTypes.bool,
  model: PropTypes.any,
  modelAngles: PropTypes.any,
  options: PropTypes.any,
  theme: PropTypes.any,
};

export default ProductImageCarouselFullScreen;
